import { supabase } from "../../../lib/supabase";
import { extractFunctionErrorMessage } from "../../../services/edgeFunctionErrors";

// Transports that require a configured destination_config.test block before
// "Send Test" can run. cj_graphql is exempt — its test path is the fixed
// /graphqltest endpoint, always available with no separate configuration.
const TRANSPORTS_REQUIRING_TEST_CONFIG = ["sftp", "http_get", "http_post"];

const NOT_CONFIGURED = { data: null, error: { message: "Not connected to database." } };

// ---------------------------------------------------------------------------
// export_templates (support-owned; also readable by advertisers picking a
// template when creating a job — RLS restricts writes to support only)
// ---------------------------------------------------------------------------

export async function fetchTemplates({ activeOnly = true } = {}) {
  if (!supabase) return NOT_CONFIGURED;
  let query = supabase
    .schema("affiliate_exports")
    .from("export_templates")
    .select("id, platform_name, transport_type, available_columns, base_field_mappings, base_destination_config, is_active, created_at")
    .order("platform_name", { ascending: true });
  if (activeOnly) query = query.eq("is_active", true);
  return query;
}

export async function createTemplate(payload) {
  if (!supabase) return NOT_CONFIGURED;
  return supabase.schema("affiliate_exports").from("export_templates").insert(payload).select().single();
}

export async function updateTemplate(id, payload) {
  if (!supabase) return NOT_CONFIGURED;
  const res = await supabase
    .schema("affiliate_exports")
    .from("export_templates")
    .update(payload)
    .eq("id", id)
    .select();
  if (res.error) return res;
  if (!res.data || res.data.length === 0) {
    return {
      data: null,
      error: {
        message:
          "Template was not updated — it may no longer exist, or you don't have permission to edit it (support access required).",
      },
    };
  }
  return { data: res.data[0], error: null };
}

export async function deleteTemplate(id) {
  if (!supabase) return NOT_CONFIGURED;
  return supabase.schema("affiliate_exports").from("export_templates").delete().eq("id", id);
}

// ---------------------------------------------------------------------------
// Vault-backed credential creation (DEV-2036 follow-up) — writes a raw
// credential value (SFTP password, API key, CJ token, etc.) to Supabase Vault
// via the create-export-credential Edge Function and returns only the
// resulting secret UUID. The raw value is never persisted client-side beyond
// the caller's own local form state, and is never returned by this call.
// ---------------------------------------------------------------------------

export async function createExportCredential(advertiserId, secretValue, description) {
  if (!supabase) return NOT_CONFIGURED;
  const raw = secretValue ?? "";
  // Only trim for the blank-input check — some SFTP passwords and API
  // tokens are legitimately whitespace-sensitive, so the value stored in
  // Vault must be exactly what the user typed (see the Edge Function, which
  // applies the same rule).
  if (!raw.trim()) {
    return { data: null, error: { message: "A credential value is required." } };
  }
  const { data, error } = await supabase.functions.invoke("create-export-credential", {
    body: { advertiser_id: advertiserId, secret_value: raw, description },
  });
  if (error) return { data: null, error: { message: await extractFunctionErrorMessage(error) } };
  if (!data?.credential_secret_id) {
    return { data: null, error: { message: "Credential creation did not return a secret id." } };
  }
  return { data: data.credential_secret_id, error: null };
}

// ---------------------------------------------------------------------------
// export_jobs (advertiser-owned mapping + destination config)
// ---------------------------------------------------------------------------

export async function fetchJobs(advertiserId) {
  if (!supabase || advertiserId == null) return NOT_CONFIGURED;
  return supabase
    .schema("affiliate_exports")
    .from("export_jobs")
    .select("id, advertiser_id, template_id, field_mappings, destination_config, cron_schedule, is_active, exported_through, created_at, updated_at, export_templates(id, platform_name, transport_type, available_columns, base_destination_config)")
    .eq("advertiser_id", advertiserId)
    .order("created_at", { ascending: false });
}

export async function fetchJob(jobId) {
  if (!supabase || !jobId) return NOT_CONFIGURED;
  return supabase
    .schema("affiliate_exports")
    .from("export_jobs")
    .select("id, advertiser_id, template_id, field_mappings, destination_config, cron_schedule, is_active, created_at, updated_at, export_templates(id, platform_name, transport_type, available_columns, base_field_mappings, base_destination_config)")
    .eq("id", jobId)
    .maybeSingle();
}

export async function createJob(payload) {
  if (!supabase) return NOT_CONFIGURED;
  return supabase.schema("affiliate_exports").from("export_jobs").insert(payload).select().single();
}

export async function updateJob(id, payload) {
  if (!supabase) return NOT_CONFIGURED;
  return supabase.schema("affiliate_exports").from("export_jobs").update(payload).eq("id", id).select().single();
}

export async function deleteJob(id) {
  if (!supabase) return NOT_CONFIGURED;
  return supabase.schema("affiliate_exports").from("export_jobs").delete().eq("id", id);
}

// exportedThrough is optional: pass it (an ISO string) only when the caller
// is setting the watermark in the same write as activation — a never-run
// job (exported_through IS NULL) can't be activated on its own (082's
// trg_export_jobs_watermark_required_when_active trigger rejects it), so
// ExportJobsPanel's Resume flow asks for a start date first and sends both
// fields together here. Pausing, and resuming a job that already has a
// watermark, omit the third argument and behave exactly as before.
export async function toggleJobActive(id, isActive, exportedThrough) {
  if (!supabase) return NOT_CONFIGURED;
  const payload = exportedThrough !== undefined ? { is_active: isActive, exported_through: exportedThrough } : { is_active: isActive };
  return supabase.schema("affiliate_exports").from("export_jobs").update(payload).eq("id", id).select().single();
}

// ---------------------------------------------------------------------------
// export_job_executions + export_job_ledger (cron-run history, per-event drill-down)
// ---------------------------------------------------------------------------

const EXECUTIONS_PAGE_SIZE = 20;
const LEDGER_PAGE_SIZE = 50;

export async function fetchExecutions(jobId, page = 0) {
  if (!supabase || !jobId) return NOT_CONFIGURED;
  const from = page * EXECUTIONS_PAGE_SIZE;
  const to = from + EXECUTIONS_PAGE_SIZE - 1;
  return supabase
    .schema("affiliate_exports")
    .from("export_job_executions")
    .select(
      "id, export_job_id, status, rows_exported, rows_failed, error_message, response_body, started_at, completed_at, created_at, run_type, rerun_id, export_job_reruns(window_from, window_to, status)",
      { count: "exact" }
    )
    .eq("export_job_id", jobId)
    .order("created_at", { ascending: false })
    .range(from, to);
}

export async function fetchLedgerRows(executionId, page = 0) {
  if (!supabase || !executionId) return NOT_CONFIGURED;
  const from = page * LEDGER_PAGE_SIZE;
  const to = from + LEDGER_PAGE_SIZE - 1;
  return supabase
    .schema("affiliate_exports")
    .from("export_job_ledger")
    .select("id, export_job_execution_id, export_job_id, source_event_id, status, error_detail, sent_at", { count: "exact" })
    .eq("export_job_execution_id", executionId)
    .order("sent_at", { ascending: true })
    .range(from, to);
}

// ---------------------------------------------------------------------------
// export_test_runs — "Send Test" on-demand dummy-data run
// ---------------------------------------------------------------------------

// App-level guard (mirrors the design doc's "Test-config presence validation"
// decision: the DB trigger deliberately does NOT enforce "test must exist",
// only that whichever transport-specific fields are present are well-formed.
// This function is the workflow-level gate that DOES require it, for the one
// action ("Send Test") that actually needs a test destination to exist.
export function assertTestDestinationConfigured(job) {
  const transportType = job?.export_templates?.transport_type ?? job?.destination_config?.transport_type;
  if (!TRANSPORTS_REQUIRING_TEST_CONFIG.includes(transportType)) {
    return { ok: true };
  }
  const hasTest = !!job?.destination_config && typeof job.destination_config === "object" && job.destination_config.test && typeof job.destination_config.test === "object";
  if (!hasTest) {
    return {
      ok: false,
      message: "A test destination must be configured before you can send a test. Open the job's destination settings and fill in the Test section.",
    };
  }
  return { ok: true };
}

// Marker prefix used by affiliate_exports.trigger_send_test() (059) to
// signal "this job's test destination matches production — resend with
// confirmed:true" instead of a hard failure. Never a real hard-failure
// message, so string-prefix matching is safe here.
const CONFIRMATION_REQUIRED_PREFIX = "CONFIRMATION_REQUIRED";

export function isConfirmationRequiredError(error) {
  return !!error?.message?.includes(CONFIRMATION_REQUIRED_PREFIX);
}

// Triggers an on-demand "Send Test" run using dummy data through the real
// mapping/transport pipeline against destination_config.test (or CJ's fixed
// /graphqltest). All authorization, same-destination confirmation gating,
// and triggered_by attribution happen server-side in the
// trigger_send_test() RPC (059) — direct INSERT into export_test_runs is
// revoked, so this RPC is the only path. assertTestDestinationConfigured()
// below is just a client-side pre-check for a nicer error message; the RPC
// enforces the same rule regardless.
export async function triggerSendTest(job, { confirmed = false } = {}) {
  if (!supabase) return NOT_CONFIGURED;
  const guard = assertTestDestinationConfigured(job);
  if (!guard.ok) {
    return { data: null, error: { message: guard.message } };
  }
  return supabase
    .schema("affiliate_exports")
    .rpc("trigger_send_test", { p_export_job_id: job.id, p_confirmed: confirmed })
    .single();
}

export async function fetchTestRuns(jobId) {
  if (!supabase || !jobId) return NOT_CONFIGURED;
  return supabase
    .schema("affiliate_exports")
    .from("export_test_runs")
    .select("id, export_job_id, status, confirmed_real_destination, response_body, error_message, triggered_by, started_at, completed_at, created_at")
    .eq("export_job_id", jobId)
    .order("created_at", { ascending: false });
}

// ---------------------------------------------------------------------------
// export_job_reruns — support-only manual re-run of real assisted_conversions
// data for an explicit past [window_from, window_to) range (074). Mirrors
// the Send Test path's shape: a read-only preflight for the UI's advisory
// preview, a support-only SECURITY DEFINER trigger RPC that is the only
// creation path, and a read model for the history panel/badge.
// ---------------------------------------------------------------------------

// Read-only advisory preview — window_from/window_to are ISO 8601 strings
// (or Date objects; supabase-js serializes either). Returns the RPC's jsonb
// payload directly ({ blocking, advisories, ... }), not wrapped in an array,
// since preflight_export_rerun() returns a scalar jsonb value, not a table
// row.
export async function preflightExportRerun(jobId, windowFrom, windowTo) {
  if (!supabase) return NOT_CONFIGURED;
  return supabase
    .schema("affiliate_exports")
    .rpc("preflight_export_rerun", {
      p_export_job_id: jobId,
      p_window_from: windowFrom,
      p_window_to: windowTo,
    });
}

// The only creation path for export_job_reruns (direct INSERT is not
// granted to authenticated — see 074). Raises CONFIRMATION_REQUIRED when
// non-blocking advisories are present and confirmed is false, mirroring
// trigger_send_test's sentinel — reuse isConfirmationRequiredError to detect
// it. distinctIdTargetKeys is scoped to this run only; it is never written
// to the job's stored field_mappings.
export async function triggerExportRerun(jobId, windowFrom, windowTo, distinctIdTargetKeys = [], confirmed = false) {
  if (!supabase) return NOT_CONFIGURED;
  return supabase
    .schema("affiliate_exports")
    .rpc("trigger_export_rerun", {
      p_export_job_id: jobId,
      p_window_from: windowFrom,
      p_window_to: windowTo,
      p_distinct_id_target_keys: distinctIdTargetKeys,
      p_confirmed: confirmed,
    })
    .single();
}

export async function fetchReruns(jobId) {
  if (!supabase || !jobId) return NOT_CONFIGURED;
  return supabase
    .schema("affiliate_exports")
    .from("export_job_reruns")
    .select("id, export_job_id, window_from, window_to, distinct_id_target_keys, confirmed_advisories, status, error_message, triggered_by, started_at, completed_at, created_at")
    .eq("export_job_id", jobId)
    .order("created_at", { ascending: false });
}
